import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js';
import React, { forwardRef, useEffect, useState } from 'react';

import { ReactComponent as DeleteIcon } from '../../../assets/icons/delete-icon-red.svg';
import BatteryLoader from '../../../shared-components/battery-loader/BatteryLoader';
import CustomBarChart from '../../../shared-components/custom-bar-chart/CustomBarChart';
import CustomDashboardMap from '../../../shared-components/custom-dashboard-map/CustomDashboardMap';
import CustomHeatmapChart from '../../../shared-components/custom-heatmap-chart/CustomHeatmapChart';
import CustomLineChart from '../../../shared-components/custom-line-chart/CustomLineChart';
import CustomPieChart from '../../../shared-components/custom-pie-chart/CustomPieChart';
import LiveAlertsList from '../../../shared-components/live-alerts-list/LiveAlertsList';
import customDashboardApi from '../../../utils/api/custom-dashboard';
import dashboardApi from '../../../utils/api/dashboard';
import endConsumer from '../../../utils/api/end-consumer';
import endConsumerV2 from '../../../utils/api/end-consumer-v2';

import DashboardKPICard from './DashboardKPICard';
import '../scss/draggable-chart-widget.scss';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

interface DraggableChartWidgetProperties {
  id: string;
  type: string;
  data: any;
  heading: string;
  onRemove?: (id: string) => void;
  // Props passed by react-grid-layout
  style?: React.CSSProperties;
  className?: string;
  onMouseDown?: React.MouseEventHandler;
  onMouseUp?: React.MouseEventHandler;
  onTouchEnd?: React.TouchEventHandler;
  children?: React.ReactNode;
  viewonly?: boolean;
  filters?: {
    facilityIds: string[];
    stationIds: string[];
    deviceIds: string[];
    type: string[];
  };
  dateDuration?: string;
}

/**
 * Transforms chart.js data format to CustomPieChart data format.
 * @param {any} chartData - The chart data.
 * @returns {any[]} The transformed data.
 */
const transformToPieData = (chartData: any) => {
  if (!chartData || !chartData.labels || !chartData.datasets) return [];
  const dataset = chartData.datasets[0];
  return chartData.labels.map((label: string, index: number) => ({
    name: label,
    value: dataset.data[index],
    color: Array.isArray(dataset.backgroundColor) ? dataset.backgroundColor[index] : dataset.backgroundColor,
  }));
};

/**
 * Transforms chart.js data format to CustomBarChart data format.
 * @param {any} chartData - The chart data.
 * @returns {any[]} The transformed data.
 */
const transformToBarData = (chartData: any) => {
  console.log('chartData', chartData);
  if (!chartData || !chartData.labels || !chartData.datasets) return [];
  const dataset = chartData.datasets[0];
  return chartData.labels.map((label: string, index: number) => ({
    name: label,
    value: dataset.data[index],
    status: dataset.status ? dataset.status[index] : undefined,
    color: Array.isArray(dataset.backgroundColor) ? dataset.backgroundColor[index] : dataset.backgroundColor,
  }));
};

/**
 * Transforms chart.js data format to CustomLineChart data format.
 * @param {any} chartData - The chart data.
 * @returns {any[]} The transformed data.
 */
const transformToLineData = (chartData: any) => {
  if (!chartData || !chartData.labels || !chartData.datasets) return [];
  return chartData.labels.map((label: string, index: number) => {
    const dataPoint: any = { label };
    chartData.datasets.forEach((dataset: any) => {
      dataPoint[dataset.label.toLowerCase()] = dataset.data[index];
    });
    return dataPoint;
  });
};

/**
 * Transforms chart.js data format to CustomHeatmapChart data format.
 * @param {any} chartData - The chart data.
 * @returns {any[]} The transformed data.
 */
const transformToHeatmapData = (chartData: any) => {
  if (!chartData || !chartData.datasets) return [];
  return chartData.datasets.map((dataset: any) => ({
    name: dataset.label,
    data: dataset.data,
  }));
};

/**
 * Extracts line chart configuration from chart.js data format.
 * @param {any} chartData - The chart data.
 * @returns {any[]} The series configuration.
 */
const getLineChartConfiguration = (chartData: any) => {
  if (!chartData || !chartData.datasets) return [];
  return chartData.datasets.map((dataset: any) => {
    const color = dataset.borderColor || dataset.pointBackgroundColor || '#F79009';
    return {
      key: dataset.label.toLowerCase(),
      name: dataset.label,
      color,
      gradientColors: [color, '#2D9FA8'],
    };
  });
};

/**
 * Helper component to render the map view using Google Maps.
 */
function MapWidgetView({ data, viewonly, filters }: { data: any, viewonly: boolean, filters?: any }) {
  return (
    <CustomDashboardMap
      filters={filters}
      isWidget
      viewonly={viewonly}
      staticData={data}
    />
  );
}

MapWidgetView.defaultProps = {
  filters: {
    facilityIds: [],
    stationIds: [],
    deviceIds: [],
    type: ['commercial'],
  },
};

/**
 * DraggableChartWidget renders a chart that can be moved and resized.
 * Wrapped in forwardRef for react-grid-layout compatibility.
 */
const DraggableChartWidget = forwardRef<HTMLDivElement, DraggableChartWidgetProperties>(({
  id,
  type,
  data,
  heading,
  onRemove,
  style = {},
  className = '',
  onMouseDown = () => { },
  onMouseUp = () => { },
  onTouchEnd = () => { },
  children = null,
  viewonly = false,
  filters,
  dateDuration,
}, reference) => {
  const [fetchedData, setFetchedData] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    const isKPI = type === 'count_card' || type.toLowerCase().includes('kpi');
    const isMap = type === 'map';

    if (viewonly && !isKPI && !isMap) {
      /**
       * Fetches real-time data for the widget based on its heading.
       */
      const fetchWidgetData = async () => {
        setLoading(true);
        try {
          const query: any = {
            timePeriod: dateDuration,
            type: filters?.type,
            hardRefresh: true,
          };
          if (filters?.facilityIds && filters?.facilityIds?.length > 0) {
            query.accountCustomId = filters?.facilityIds;
          }
          if (filters?.stationIds && filters?.stationIds?.length > 0) {
            query.stationIds = filters?.stationIds;
          }
          if (filters?.deviceIds && filters?.deviceIds?.length > 0) {
            query.deviceIds = filters?.deviceIds;
          }
          const stationQuery: any = {
            hardRefresh: true,
          };
          const operationsQuery: any = {
            timePeriod: dateDuration,
          };
          let response;
          const h = heading.toLowerCase();

          if (h.includes('active vs inactive facility accounts')) {
            response = await customDashboardApi.getDashboardFacilitiesAccountStatus(query);
          } else if (h.includes('top 10 facility accounts by revenue')) {
            response = await customDashboardApi.getTop10FacilityAccountsByRevenue(query);
          } else if (h.includes('facility accounts growth over time')) {
            response = await customDashboardApi.getFacilityAccountsGrowthOverTime(query);
          } else if (h.includes('facility accounts pending vs approved')) {
            response = await customDashboardApi.getDashboardFacilitiesAccountStatus({ ...query, showDetailed: true });
          } else if (h.includes('active vs inactive end consumers')) {
            response = await endConsumerV2.fetchEndConsumerStatus();
          } else if (h.includes('top 10 end consumers by usage')) {
            response = await customDashboardApi.getTop10PowerConsumedEnconsumer(query);
          } else if (h.includes('consumer growth trend')) {
            let timePeriod;
            switch (dateDuration) {
              case 'last24hours':
                timePeriod = 'hour'
                break;
              case 'last7days':
                timePeriod = 'week'
                break;
              case 'lastMonth':
                timePeriod = 'month'
                break;
              case 'last12months':
                timePeriod = 'year'
                break;
              default:
                timePeriod = 'year'
                break;
            }
            response = await endConsumer.endConsumerCountReport({ type: timePeriod });
          } else if (h.includes('station availability status')) {
            if (filters?.facilityIds && filters?.facilityIds?.length > 0) {
              stationQuery.accountCustomId = filters?.facilityIds;
            }
            response = await customDashboardApi.getStationAvailabilityStatus(stationQuery);
          } else if (h.includes('utilization rate by station')) {
            if (filters?.facilityIds && filters?.facilityIds?.length > 0) {
              stationQuery.accountCustomId = filters?.facilityIds;
            }
            response = await customDashboardApi.getUtilizationRateByStation(stationQuery);
          } else if (h.includes('highest revenue generating stations')) {
            if (filters?.facilityIds && filters?.facilityIds?.length > 0) {
              stationQuery.accountCustomId = filters?.facilityIds;
            }
            response = await customDashboardApi.getTop10StationsByRevenue(stationQuery);
          } else if (h.includes('peak usage hours')) {
            if (filters?.facilityIds && filters?.facilityIds?.length > 0) {
              stationQuery.accountCustomId = filters?.facilityIds;
            }
            stationQuery.timePeriod = dateDuration;
            response = await customDashboardApi.getPeakUsageHours(stationQuery);
          } else if (h.includes('charger online/offline status')) {
            if (filters?.facilityIds && filters?.facilityIds?.length > 0) {
              stationQuery.accountCustomId = filters?.facilityIds;
            }
            stationQuery.type = filters?.type;
            response = await dashboardApi.getDeviceStatusReport(stationQuery);
          } else if (h.includes('session type distribution')) {
            if (filters?.facilityIds && filters?.facilityIds?.length > 0) {
              stationQuery.accountCustomId = filters?.facilityIds;
            }
            response = await customDashboardApi.getGuestVsPaidSessions(stationQuery);
          } else if (h.includes('session purpose distribution')) {
            if (filters?.facilityIds && filters?.facilityIds?.length > 0) {
              stationQuery.accountCustomId = filters?.facilityIds;
            }
            response = await customDashboardApi.getPersonalVsCommercialSessions(stationQuery);
          } else if (h.includes('successful vs failed transactions')) {
            if (filters?.facilityIds && filters?.facilityIds?.length > 0) {
              operationsQuery.accountCustomId = filters?.facilityIds;
            }
            operationsQuery.hardRefresh = true;
            response = await customDashboardApi.getSuccessfulVsFailedTransactions(operationsQuery);
          } else if (h.includes('transactions over time')) {
            if (filters?.facilityIds && filters?.facilityIds?.length > 0) {
              operationsQuery.accountCustomId = filters?.facilityIds;
            }
            operationsQuery.hardRefresh = true;
            response = await customDashboardApi.getTransactionsOverTime(operationsQuery);
          } else if (h.includes('revenue trend')) {
            if (filters?.facilityIds && filters?.facilityIds?.length > 0) {
              operationsQuery.accountCustomId = filters?.facilityIds;
            }
            operationsQuery.hardRefresh = true;
            response = await customDashboardApi.getRevenueTrend(operationsQuery);
          } else if (h.includes('revenue vs payable vs payout')) {
            if (filters?.facilityIds && filters?.facilityIds?.length > 0) {
              operationsQuery.accountCustomId = filters?.facilityIds;
            }
            delete operationsQuery.timePeriod;
            response = await customDashboardApi.getRevenueVsPayableVsPayout(operationsQuery);
          } else if (h.includes('pending vs completed payouts')) {
            if (filters?.facilityIds && filters?.facilityIds?.length > 0) {
              operationsQuery.accountCustomId = filters?.facilityIds;
            }
            operationsQuery.hardRefresh = true;
            response = await customDashboardApi.getPendingVsCompletedPayouts(operationsQuery);
          } else if (h.includes('payouts over time')) {
            if (filters?.facilityIds && filters?.facilityIds?.length > 0) {
              operationsQuery.accountCustomId = filters?.facilityIds;
            }
            operationsQuery.hardRefresh = true;
            response = await customDashboardApi.getPayoutsOverTime(operationsQuery);
          } else if (h.includes('top 10 facility accounts by payout volume')) {
            if (filters?.facilityIds && filters?.facilityIds?.length > 0) {
              operationsQuery.accountCustomId = filters?.facilityIds;
            }
            response = await customDashboardApi.getTop10FacilityAccountsByPayoutVolume(operationsQuery);
          } else if (h.includes('total penalties issued over time')) {
            if (filters?.facilityIds && filters?.facilityIds?.length > 0) {
              operationsQuery.accountCustomId = filters?.facilityIds;
            }
            operationsQuery.hardRefresh = true;
            response = await customDashboardApi.getTotalPenaltiesIssuedOverTime(operationsQuery);
          } else if (h.includes('penalty revenue over time')) {
            if (filters?.facilityIds && filters?.facilityIds?.length > 0) {
              operationsQuery.accountCustomId = filters?.facilityIds;
            }
            operationsQuery.hardRefresh = true;
            response = await customDashboardApi.getPenaltyRevenueOverTime(operationsQuery);
          } else if (h.includes('penalties by station')) {
            if (filters?.facilityIds && filters?.facilityIds?.length > 0) {
              operationsQuery.accountCustomId = filters?.facilityIds;
            }
            operationsQuery.hardRefresh = true;
            response = await customDashboardApi.getPenaltiesByStation(operationsQuery);
          }

          if (response?.status === 200) {
            let finalData = response.data;
            if (h.includes('active vs inactive facility accounts')) {
              finalData = {
                labels: ['Active', 'Inactive'],
                totalLabel: 'Total Accounts',
                datasets: [
                  {
                    label: 'Facility Accounts',
                    data: [response.data.active, response.data.inactive],
                    backgroundColor: ['rgba(84, 242, 147, 0.7)', 'rgba(242, 84, 84, 0.7)'],
                    borderColor: ['rgba(84, 242, 147, 1)', 'rgba(242, 84, 84, 1)'],
                    borderWidth: 1,
                  },
                ],
              };
            } else if (h.includes('top 10 facility accounts by revenue')) {
              const labels = response.data.topAccounts?.map((accumulator: any) => accumulator.accountName) || [];
              const revenueData = response.data.topAccounts?.map((accumulator: any) => parseFloat(accumulator.totalRevenue)) || [];
              finalData = {
                labels,
                datasets: [
                  {
                    label: 'Revenue',
                    data: revenueData,
                    backgroundColor: 'rgba(54, 162, 235, 0.7)',
                    borderColor: 'rgba(54, 162, 235, 1)',
                    borderWidth: 1,
                  },
                ],
              };
            } else if (h.includes('facility accounts growth over time')) {
              const labels = response.data?.map((item: any) => item.label) || [];
              const revenueData = response.data?.map((item: any) => parseFloat(item.totalRevenue)) || [];
              finalData = {
                labels,
                datasets: [
                  {
                    label: 'Revenue',
                    data: revenueData,
                    backgroundColor: 'rgba(75, 192, 192, 0.7)',
                    borderColor: 'rgba(75, 192, 192, 1)',
                    borderWidth: 1,
                  },
                ],
              };
            } else if (h.includes('facility accounts pending vs approved')) {
              finalData = {
                labels: ['Approved', 'Pending'],
                totalLabel: 'Total Accounts',
                datasets: [
                  {
                    label: 'Facility Accounts',
                    data: [response.data.approved, response.data.pending],
                    backgroundColor: ['rgba(84, 242, 147, 0.7)', 'rgba(255, 193, 7, 0.7)'],
                    borderColor: ['rgba(84, 242, 147, 1)', 'rgba(255, 193, 7, 1)'],
                    borderWidth: 1,
                  },
                ],
              };
            } else if (h.includes('top 10 end consumers by usage')) {
              const labels = response.data?.map((item: any) => item.userName) || [];
              const usageData = response.data?.map((item: any) => parseFloat(item.totalPower)) || [];
              finalData = {
                labels,
                datasets: [
                  {
                    label: 'Usage (kW)',
                    data: usageData,
                    backgroundColor: 'rgba(255, 159, 64, 0.7)',
                    borderColor: 'rgba(255, 159, 64, 1)',
                    borderWidth: 1,
                  },
                ],
              };
            } else if (h.includes('active vs inactive end consumers')) {
              finalData = {
                labels: ['Active', 'Inactive'],
                total: response.data.totalEndConsumers,
                totalLabel: 'Total Consumers',
                datasets: [
                  {
                    label: 'End Consumers',
                    data: [response.data.activeEndConsumers, response.data.inactiveEndConsumers],
                    backgroundColor: ['rgba(84, 242, 147, 0.7)', 'rgba(242, 84, 84, 0.7)'],
                    borderColor: ['rgba(84, 242, 147, 1)', 'rgba(242, 84, 84, 1)'],
                    borderWidth: 1,
                  },
                ],
              };
            } else if (h.includes('consumer growth trend')) {
              const labels = response.data?.map((item: any) => item.month) || [];
              const userData = response.data?.map((item: any) => item.users) || [];
              finalData = {
                labels,
                datasets: [
                  {
                    label: 'Users',
                    data: userData,
                    borderColor: 'rgba(54, 162, 235, 1)',
                    backgroundColor: 'rgba(54, 162, 235, 0.2)',
                    fill: true,
                  },
                ],
              };
            } else if (h.includes('station availability status')) {
              finalData = {
                labels: ['Available', 'Unavailable'],
                totalLabel: 'Total Stations',
                datasets: [
                  {
                    label: 'Station Availability',
                    data: [response.data.results.available, response.data.results.unavailable],
                    backgroundColor: ['rgba(84, 242, 147, 0.7)', 'rgba(242, 84, 84, 0.7)'],
                    borderColor: ['rgba(84, 242, 147, 1)', 'rgba(242, 84, 84, 1)'],
                    borderWidth: 1,
                  },
                ],
              };
            } else if (h.includes('utilization rate by station')) {
              const labels = response.data.results?.map((accumulator: any) => accumulator.stationName) || [];
              const revenueData = response.data.results?.map((accumulator: any) => parseFloat(accumulator.totalPowerConsumed)) || [];
              finalData = {
                labels,
                datasets: [
                  {
                    label: 'Total Power Consumed',
                    data: revenueData,
                    backgroundColor: '#2E9E3A',
                    borderColor: '#2E9E3A',
                    borderWidth: 1,
                  },
                ],
              };
            } else if (h.includes('highest revenue generating stations')) {
              const labels = response.data.results?.map((accumulator: any) => accumulator.stationName) || [];
              const revenueData = response.data.results?.map((accumulator: any) => parseFloat(accumulator.totalRevenue)) || [];
              finalData = {
                labels,
                datasets: [
                  {
                    label: 'Revenue',
                    data: revenueData,
                    backgroundColor: 'rgba(54, 162, 235, 0.7)',
                    borderColor: 'rgba(54, 162, 235, 1)',
                    borderWidth: 1,
                  },
                ],
              };
            } else if (h.includes('peak usage hours')) {
              const xAxisLabels = Array.from({ length: 24 }, (_, index) => index.toString().padStart(2, '0'));
              finalData = {
                labels: xAxisLabels,
                datasets: response.data?.results?.map((item: any) => ({
                  label: item.name,
                  data: item.data,
                })) || [],
              };
            } else if (h.includes('charger online/offline status')) {
              finalData = {
                labels: ['Online', 'Offline'],
                totalLabel: 'Total Charger',
                datasets: [
                  {
                    label: 'Charger Status',
                    data: response.data.finalResult.map((item: any) => item.count),
                    backgroundColor: ['rgba(84, 242, 147, 0.7)', 'rgba(242, 84, 84, 0.7)'],
                    borderColor: ['rgba(84, 242, 147, 1)', 'rgba(242, 84, 84, 1)'],
                    borderWidth: 1,
                  },
                ],
              };
            } else if (h.includes('session type distribution')) {
              finalData = {
                labels: ['Guest', 'Paid'],
                totalLabel: 'Total Sessions',
                datasets: [
                  {
                    label: 'Session Type',
                    data: [response.data.guestSessions, response.data.paidSessions],
                    backgroundColor: ['rgba(84, 242, 147, 0.7)', 'rgba(242, 84, 84, 0.7)'],
                    borderColor: ['rgba(84, 242, 147, 1)', 'rgba(242, 84, 84, 1)'],
                    borderWidth: 1,
                  },
                ],
              };
            } else if (h.includes('session purpose distribution')) {
              finalData = {
                labels: ['Personal', 'Commercial'],
                totalLabel: 'Total Sessions',
                datasets: [
                  {
                    label: 'Session Type',
                    data: [response.data.personalSessions, response.data.commercialSessions],
                    backgroundColor: ['rgba(84, 242, 147, 0.7)', 'rgba(242, 84, 84, 0.7)'],
                    borderColor: ['rgba(84, 242, 147, 1)', 'rgba(242, 84, 84, 1)'],
                    borderWidth: 1,
                  },
                ],
              };
            } else if (h.includes('successful vs failed transactions')) {
              finalData = {
                labels: ['Successful', 'Failed'],
                totalLabel: 'Total Transactions',
                datasets: [
                  {
                    label: 'Transaction Type',
                    data: [response.data.results.successful, response.data.results.failed],
                    backgroundColor: ['rgba(84, 242, 147, 0.7)', 'rgba(242, 84, 84, 0.7)'],
                    borderColor: ['rgba(84, 242, 147, 1)', 'rgba(242, 84, 84, 1)'],
                    borderWidth: 1,
                  },
                ],
              };
            } else if (h.includes('transactions over time')) {
              const labels = response.data?.results?.map((item: any) => item.label) || [];
              const userData = response.data?.results?.map((item: any) => item.totalCount) || [];
              finalData = {
                labels,
                datasets: [
                  {
                    label: 'Transactions',
                    data: userData,
                    borderColor: 'rgba(54, 162, 235, 1)',
                    backgroundColor: 'rgba(54, 162, 235, 0.2)',
                    fill: true,
                  },
                ],
              };
            } else if (h.includes('revenue trend')) {
              const labels = response.data?.results?.map((item: any) => item.label) || [];
              const userData = response.data?.results?.map((item: any) => item.totalRevenue) || [];
              finalData = {
                labels,
                datasets: [
                  {
                    label: 'Revenue',
                    data: userData,
                    borderColor: '#2E9E3A',
                    backgroundColor: '#EDEDED00',
                    fill: true,
                  },
                ],
              };
            } else if (h.includes('revenue vs payable vs payout')) {
              const labels = response.data?.map((item: any) => item.monthName) || [];
              const revenueData = response.data?.map((item: any) => item.totalRevenue) || [];
              const payableData = response.data?.map((item: any) => item.totalPayableAmount) || [];
              const payoutData = response.data?.map((item: any) => item.totalAmountPaid) || [];
              finalData = {
                labels,
                datasets: [
                  {
                    label: 'Revenue',
                    data: revenueData,
                    borderColor: 'rgba(54, 162, 235, 1)',
                    backgroundColor: 'rgba(54, 162, 235, 0.2)',
                    fill: true,
                  },
                  {
                    label: 'Payable',
                    data: payableData,
                    borderColor: '#2E9E3A',
                    backgroundColor: '#EDEDED00',
                    fill: true,
                  },
                  {
                    label: 'Payout',
                    data: payoutData,
                    borderColor: '#ef7c12ff',
                    backgroundColor: 'rgba(239, 124, 18, 0.2)',
                    fill: true,
                  },
                ],
              };
            } else if (h.includes('pending vs completed payouts')) {
              finalData = {
                labels: response?.data?.results?.map((item: any) => item.label) || [],
                totalLabel: 'Total Payouts',
                datasets: [
                  {
                    label: 'Payout Status',
                    data: response?.data?.results?.map((item: any) => item.value) || [],
                    backgroundColor: ['rgba(84, 242, 147, 0.7)', 'rgba(242, 84, 84, 0.7)'],
                    borderColor: ['rgba(84, 242, 147, 1)', 'rgba(242, 84, 84, 1)'],
                    borderWidth: 1,
                  },
                ],
              };
            } else if (h.includes('payouts over time')) {
              finalData = {
                labels: response?.data?.results?.map((item: any) => item.label) || [],
                totalLabel: 'Total Payouts',
                datasets: [
                  {
                    label: 'Payouts',
                    data: response?.data?.results?.map((item: any) => item.value) || [],
                    backgroundColor: '#2D9FA8',
                    borderColor: '#2D9FA8',
                    borderWidth: 1,
                  },
                ],
              };
            } else if (h.includes('top 10 facility accounts by payout volume')) {
              const labels = response.data?.results.map((item: any) => item.label) || [];
              const usageData = response.data?.results.map((item: any) => parseFloat(item.value)) || [];
              finalData = {
                labels,
                datasets: [
                  {
                    label: 'Payout Volume',
                    data: usageData,
                    backgroundColor: '#2D9FA8',
                    borderColor: '#2D9FA8',
                    borderWidth: 1,
                  },
                ],
              };
            } else if (h.includes('total penalties issued over time')) {
              finalData = {
                labels: response?.data?.results?.map((item: any) => item.label) || [],
                totalLabel: 'Total Penalties',
                datasets: [
                  {
                    label: 'Penalties',
                    data: response?.data?.results?.map((item: any) => item.value) || [],
                    backgroundColor: '#2D9FA8',
                    borderColor: '#2D9FA8',
                    borderWidth: 1,
                  },
                ],
              };
            } else if (h.includes('penalty revenue over time')) {
              finalData = {
                labels: response?.data?.results?.map((item: any) => item.label) || [],
                totalLabel: 'Total Penalties Revenue',
                datasets: [
                  {
                    label: 'Penalties Revenue',
                    data: response?.data?.results?.map((item: any) => item.value) || [],
                    backgroundColor: '#da8113ff',
                    borderColor: '#a48759ff',
                    borderWidth: 1,
                  },
                ],
              };
            } else if (h.includes('penalties by station')) {
              const labels = response.data?.results.map((item: any) => item.stationName) || [];
              const usageData = response.data?.results.map((item: any) => parseFloat(item.totalPenaltyRevenue)) || [];
              finalData = {
                labels,
                datasets: [
                  {
                    label: 'Penalties',
                    data: usageData,
                    backgroundColor: '#de7915ff',
                    borderColor: '#de7915ff',
                    borderWidth: 1,
                  },
                ],
              };
            }
            setFetchedData(finalData);
          }
        } catch (error) {
          console.error(`Error fetching data for ${heading}:`, error);
        } finally {
          setLoading(false);
        }
      };

      fetchWidgetData();
    }
  }, [viewonly, heading, type, filters, dateDuration]);

  const displayData = fetchedData || data;

  console.log('heading', heading);
  /**
   * Renders the chart based on the specified type.
   * @returns {React.ReactElement} The chart component.
   */
  const renderChart = () => {
    const t = type.toLowerCase();
    if (t === 'count_card' || t.includes('kpi')) {
      let kpiType: 'revenue' | 'energy' | 'activeDevices' | 'endConsumers' = 'revenue';
      if (heading.toLowerCase().includes('energy')) kpiType = 'energy';
      else if (heading.toLowerCase().includes('devices')) kpiType = 'activeDevices';
      else if (heading.toLowerCase().includes('consumers')) kpiType = 'endConsumers';
      return <DashboardKPICard type={kpiType} isWidget dateDuration={dateDuration} filters={filters} />;
    }

    if (t.includes('pie') || t.includes('donut')) {
      const isDonut = t.includes('donut');
      return (
        <CustomPieChart
          data={transformToPieData(displayData)}
          totalLabel={displayData?.totalLabel || 'Total'}
          height="100%"
          innerRadius={isDonut ? 60 : 0}
          totalValue={displayData?.total}
        />
      );
    }

    if (t.includes('vertical bar') || t.includes('horizontal bar')) {
      return (
        <CustomBarChart
          data={transformToBarData(displayData)}
          // height="100%"
          layout={t.includes('horizontal bar') || t.includes('performance') ? 'vertical' : 'horizontal'}
        />
      );
    }
    if (t.includes('line')) {
      return (
        <CustomLineChart
          data={transformToLineData(displayData)}
          height="100%"
          series={getLineChartConfiguration(displayData)}
        />
      );
    }
    if (t.includes('heatmap')) {
      return (
        <CustomHeatmapChart
          series={transformToHeatmapData(displayData)}
          xaxisLabels={displayData?.labels}
          height="100%"
        />
      );
    }
    if (t === 'alert_list') {
      return (
        <div style={{ height: '100%', overflowY: 'auto' }}>
          <LiveAlertsList alerts={displayData?.alerts || displayData || []} />
        </div>
      );
    }
    if (t === 'map') return <MapWidgetView data={displayData} viewonly={viewonly} filters={filters} />;

    return <div>Unsupported Chart Type</div>;
  };

  /**
   * Renders the widget content, showing a loader if data is being fetched.
   * @returns {React.ReactElement} The content of the widget.
   */
  const renderContent = () => {
    if (loading) {
      return (
        <div className="loader-container">
          <BatteryLoader />
        </div>
      );
    }
    return renderChart();
  };

  // No temporary defaultProps object needed if we use static property

  return (
    <div
      ref={reference}
      style={style}
      className={`chart-widget-container ${className}`}
      onMouseDown={onMouseDown}
      onMouseUp={onMouseUp}
      onTouchEnd={onTouchEnd}
      role="button"
      tabIndex={0}
      onClick={() => { }}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          // Placeholder for accessibility
        }
      }}
    >
      <div className="widget-content">
        {type !== 'map' && type !== 'count_card' && !type.includes('kpi') ? (
          <h3 className="widget-title-inline">
            {heading}
          </h3>
        ) : (
          <div />
        )}
        <div
          className={`widget-body ${type === 'map' ? 'map-view' : ''} ${!viewonly ? 'no-interaction' : ''}`}
        >
          {renderContent()}
        </div>
      </div>

      {/* Delete Button - matches snippet's absolute positioning and no-drag class */}
      {onRemove && !viewonly && (
        <div className="no-drag delete-button-container">
          <DeleteIcon
            className="remove-widget-icon"
            onClick={(event) => {
              event.stopPropagation();
              onRemove(id);
            }}
            onMouseDown={(event) => event.stopPropagation()}
            role="presentation"
          />
        </div>
      )}
      {children}
    </div>
  );
});

DraggableChartWidget.displayName = 'DraggableChartWidget';

DraggableChartWidget.defaultProps = {
  style: {},
  className: '',
  /**
   * onRemove is a required prop for react-grid-layout
   */
  onRemove: () => { },
  /**
   * onMouseDown is a required prop for react-grid-layout
   */
  onMouseDown: () => { },
  /**
   * onMouseUp is a required prop for react-grid-layout
   */
  onMouseUp: () => { },
  /**
   * onTouchEnd is a required prop for react-grid-layout
   */
  onTouchEnd: () => { },
  /**
   * children is a required prop for react-grid-layout
   */
  children: null,
  /**
   * viewonly is a required prop for react-grid-layout
   */
  viewonly: false,
  filters: {
    facilityIds: [],
    stationIds: [],
    deviceIds: [],
    type: ['commercial'],
  },
  dateDuration: '',
};

export default DraggableChartWidget;
